// src/pages/Onboarding.jsx
// Multi-step onboarding: BAA -> Business Info -> Payment -> Twilio Provisioning

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, FileCheck, Building2, CreditCard, Phone, CheckCircle, ChevronRight,
  Loader2, AlertTriangle, ArrowLeft, Building, User, RefreshCw, Briefcase, Search, X,
  Upload, Settings2, Rocket, ToggleRight
} from 'lucide-react';
import { ONBOARDING_STEP_ROUTES } from '../utils/onboarding';

const BASE = process.env.REACT_APP_API_URL || '';

const brand = {
  navy: '#172A3A',
  gold: '#FFB800',
  slate: '#20344A',
};

// Step indicator component
function StepIndicator({ steps, currentStep }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((step, index) => {
        const isCompleted = steps.indexOf(currentStep) > index;
        const isCurrent = step === currentStep;

        return (
          <React.Fragment key={step}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition ${
              isCompleted ? 'bg-green-100 text-green-700' :
              isCurrent ? 'bg-yellow-100 text-yellow-700' :
              'bg-slate-100 text-slate-400'
            }`}>
              {isCompleted ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-xs">
                  {index + 1}
                </span>
              )}
              <span className="hidden sm:inline">{
                step === 'baa' ? 'BAA' :
                step === 'business_info' ? 'Business' :
                step === 'payment' ? 'Payment' :
                step === 'phone_choice' ? 'Phone' :
                step === 'twilio_setup' ? 'Twilio' :
                step === 'book_of_business' ? 'Carriers' :
                step === 'import_clients' ? 'Import' :
                step === 'review_automations' ? 'Automations' :
                step === 'completion' ? 'Done' :
                'Setup'
              }</span>
            </div>
            {index < steps.length - 1 && (
              <ChevronRight className="w-4 h-4 text-slate-300" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// BAA Step Component
function BAAStep({ onComplete }) {
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [baaData, setBaaData] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    signerName: '',
    entityType: '',
    businessName: '',
    ein: '',
    ssnLast4: ''
  });

  useEffect(() => {
    fetchBAA();
  }, []);

  const fetchBAA = async () => {
    try {
      const res = await fetch(`${BASE}/onboarding/baa`, { credentials: 'include' });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setBaaData(json.baa);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async (e) => {
    e.preventDefault();
    setSigning(true);
    setError('');

    try {
      const res = await fetch(`${BASE}/onboarding/baa/sign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (baaData?.alreadySigned) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-slate-800 mb-2">BAA Already Signed</h3>
        <p className="text-slate-600 mb-4">You signed the Business Associate Agreement on {new Date(baaData.signedAt).toLocaleDateString()}</p>
        <button
          onClick={onComplete}
          className="px-6 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition"
        >
          Continue <ChevronRight className="inline w-4 h-4 ml-1" />
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">HIPAA Privacy Agreement</h2>
        <p className="text-slate-600 mt-2">A one-time form required before accessing client health data</p>
      </div>

      {/* Plain-English explainer */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
        <p className="font-semibold mb-1">What is this and why do I need it?</p>
        <p>Because Pro IRP stores health insurance data on your behalf, federal law (HIPAA) requires both parties to sign a <strong>Business Associate Agreement</strong>. This protects you and your clients. It's a one-time form — takes about 30 seconds to complete.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* BAA Text */}
      <div className="bg-slate-50 rounded-xl p-4 mb-6 max-h-64 overflow-y-auto text-sm text-slate-700 border border-slate-200">
        <pre className="whitespace-pre-wrap font-sans">{baaData?.text}</pre>
      </div>

      {/* Signing Form */}
      <form onSubmit={handleSign} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Your Full Legal Name *</label>
          <input
            type="text"
            value={formData.signerName}
            onChange={(e) => setFormData({ ...formData, signerName: e.target.value })}
            placeholder="John Smith"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Entity Type *</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, entityType: 'business' })}
              className={`p-4 rounded-xl border-2 transition flex flex-col items-center gap-2 ${
                formData.entityType === 'business'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Building className="w-6 h-6 text-blue-600" />
              <span className="font-medium">Business</span>
              <span className="text-xs text-slate-500">LLC, Corp, etc.</span>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, entityType: 'sole_proprietor' })}
              className={`p-4 rounded-xl border-2 transition flex flex-col items-center gap-2 ${
                formData.entityType === 'sole_proprietor'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <User className="w-6 h-6 text-blue-600" />
              <span className="font-medium">Individual</span>
              <span className="text-xs text-slate-500">Sole Proprietor</span>
            </button>
          </div>
        </div>

        {formData.entityType === 'business' && (
          <>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Business Name *</label>
              <input
                type="text"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                placeholder="Your Agency LLC"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">EIN (optional)</label>
              <input
                type="text"
                value={formData.ein}
                onChange={(e) => setFormData({ ...formData, ein: e.target.value })}
                placeholder="XX-XXXXXXX"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              />
            </div>
          </>
        )}

        {formData.entityType === 'sole_proprietor' && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Last 4 Digits of SSN *</label>
            <input
              type="text"
              value={formData.ssnLast4}
              onChange={(e) => setFormData({ ...formData, ssnLast4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              placeholder="1234"
              maxLength={4}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              required
            />
            <p className="text-xs text-slate-500 mt-1">Required for A2P SMS registration verification</p>
          </div>
        )}

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm">
          <p className="text-yellow-800">
            By clicking "Sign Agreement", you acknowledge that you have read and agree to be bound by the terms of this Business Associate Agreement.
          </p>
        </div>

        <button
          type="submit"
          disabled={signing || !formData.signerName || !formData.entityType || (formData.entityType === 'business' && !formData.businessName) || (formData.entityType === 'sole_proprietor' && formData.ssnLast4?.length !== 4)}
          className="w-full py-3 rounded-xl font-bold text-lg transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: brand.gold, color: brand.navy }}
        >
          {signing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Signing...
            </>
          ) : (
            <>
              <FileCheck className="w-5 h-5" />
              Sign Agreement
            </>
          )}
        </button>
      </form>
    </div>
  );
}

// Business Info Step Component
function BusinessInfoStep({ onComplete, onBack }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    entityType: '',
    businessName: '',
    businessType: '',
    ein: '',
    ssnLast4: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    website: '',
    preferredAreaCode: ''
  });

  useEffect(() => {
    fetchExistingInfo();
  }, []);

  const fetchExistingInfo = async () => {
    try {
      const res = await fetch(`${BASE}/onboarding/business-info`, { credentials: 'include' });
      const json = await res.json();
      if (res.ok && json.businessInfo) {
        setFormData({
          entityType: json.businessInfo.entityType || '',
          businessName: json.businessInfo.businessName || '',
          businessType: json.businessInfo.businessType || '',
          ein: '',  // Don't pre-fill sensitive data
          ssnLast4: '',
          address: json.businessInfo.address || '',
          city: json.businessInfo.city || '',
          state: json.businessInfo.state || '',
          zip: json.businessInfo.zip || '',
          phone: json.businessInfo.phone || '',
          website: json.businessInfo.website || '',
          preferredAreaCode: json.businessInfo.preferredAreaCode || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch business info:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const res = await fetch(`${BASE}/onboarding/business-info`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 text-purple-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">Business Information</h2>
        <p className="text-slate-600 mt-2">Required for A2P SMS registration (FCC compliance)</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Entity Type */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Entity Type *</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, entityType: 'business', businessType: 'LLC' })}
              className={`p-4 rounded-xl border-2 transition flex flex-col items-center gap-2 ${
                formData.entityType === 'business'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <Building className="w-6 h-6 text-purple-600" />
              <span className="font-medium">Business</span>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, entityType: 'sole_proprietor', businessType: 'Sole Proprietor' })}
              className={`p-4 rounded-xl border-2 transition flex flex-col items-center gap-2 ${
                formData.entityType === 'sole_proprietor'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <User className="w-6 h-6 text-purple-600" />
              <span className="font-medium">Individual</span>
            </button>
          </div>
        </div>

        {/* Business/Individual Name */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">
            {formData.entityType === 'business' ? 'Business Name *' : 'Your Full Name *'}
          </label>
          <input
            type="text"
            value={formData.businessName}
            onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
            placeholder={formData.entityType === 'business' ? 'Your Agency LLC' : 'John Smith'}
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            required
          />
        </div>

        {formData.entityType === 'business' && (
          <>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Business Type *</label>
              <select
                value={formData.businessType}
                onChange={(e) => setFormData({ ...formData, businessType: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                required
              >
                <option value="">Select type...</option>
                <option value="LLC">LLC</option>
                <option value="Corporation">Corporation</option>
                <option value="Partnership">Partnership</option>
                <option value="S-Corp">S-Corp</option>
                <option value="Non-Profit">Non-Profit</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">EIN (optional but recommended)</label>
              <input
                type="text"
                value={formData.ein}
                onChange={(e) => setFormData({ ...formData, ein: e.target.value })}
                placeholder="XX-XXXXXXX"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              />
            </div>
          </>
        )}

        {formData.entityType === 'sole_proprietor' && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Last 4 of SSN *</label>
            <input
              type="text"
              value={formData.ssnLast4}
              onChange={(e) => setFormData({ ...formData, ssnLast4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
              placeholder="1234"
              maxLength={4}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              required
            />
          </div>
        )}

        {/* Address */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Street Address *</label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="123 Main Street"
            className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            required
          />
        </div>

        <div className="grid grid-cols-6 gap-3">
          <div className="col-span-3">
            <label className="block text-sm font-semibold text-slate-700 mb-1">City *</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              required
            />
          </div>
          <div className="col-span-1">
            <label className="block text-sm font-semibold text-slate-700 mb-1">State *</label>
            <input
              type="text"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase().slice(0, 2) })}
              placeholder="TX"
              maxLength={2}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              required
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-semibold text-slate-700 mb-1">ZIP *</label>
            <input
              type="text"
              value={formData.zip}
              onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
              placeholder="12345"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Phone Number *</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Website (optional)</label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://..."
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
          </div>
        </div>

        {/* Preferred Area Code for Phone Number */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <label className="block text-sm font-semibold text-blue-800 mb-1">
            <Phone className="w-4 h-4 inline mr-1" />
            Preferred Area Code for Your Business Line *
          </label>
          <p className="text-xs text-blue-600 mb-3">
            We'll provision a dedicated phone number with this area code for your client communications.
          </p>
          <input
            type="text"
            value={formData.preferredAreaCode}
            onChange={(e) => setFormData({ ...formData, preferredAreaCode: e.target.value.replace(/\D/g, '').slice(0, 3) })}
            placeholder="e.g. 513, 614, 212"
            maxLength={3}
            className="w-32 px-4 py-3 rounded-lg border border-blue-300 focus:ring-2 focus:ring-blue-400 focus:border-transparent text-center text-lg font-mono"
            required
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 py-3 rounded-xl font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <button
            type="submit"
            disabled={saving || !formData.entityType || !formData.businessName || !formData.address || !formData.city || !formData.state || !formData.zip || !formData.phone || !formData.preferredAreaCode || formData.preferredAreaCode.length !== 3}
            className="flex-1 py-3 rounded-xl font-bold text-lg transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: brand.gold, color: brand.navy }}
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Continue
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// Payment Step Component
function PaymentStep({ onComplete, onBack }) {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState('');

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;

    setValidating(true);
    setPromoError('');
    setPromoApplied(null);

    try {
      const res = await fetch(`${BASE}/stripe/validate-promo`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ promoCode: promoCode.trim().toUpperCase() })
      });
      const json = await res.json();

      if (!res.ok) {
        setPromoError(json.error || 'Invalid promo code');
      } else {
        setPromoApplied(json.promo);
      }
    } catch (err) {
      setPromoError('Failed to validate promo code');
    } finally {
      setValidating(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${BASE}/stripe/create-checkout-session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promoCode: promoApplied ? promoApplied.code : null
        })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);

      // Redirect to Stripe Checkout
      window.location.href = json.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Calculate displayed price
  const originalPrice = 49.95;
  let displayPrice = originalPrice;
  let discountAmount = 0;

  if (promoApplied) {
    if (promoApplied.percentOff === 100) {
      displayPrice = 0;
      discountAmount = originalPrice;
    } else if (promoApplied.percentOff) {
      discountAmount = originalPrice * (promoApplied.percentOff / 100);
      displayPrice = originalPrice - discountAmount;
    } else if (promoApplied.amountOff) {
      discountAmount = promoApplied.amountOff / 100;
      displayPrice = Math.max(0, originalPrice - discountAmount);
    }
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CreditCard className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">Complete Your Subscription</h2>
        <p className="text-slate-600 mt-2">Start your Pro IRP subscription to continue</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Promo Code Input */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-slate-700 mb-2">Promo Code (optional)</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => {
              setPromoCode(e.target.value.toUpperCase());
              setPromoError('');
              setPromoApplied(null);
            }}
            placeholder="Enter code"
            className="flex-1 px-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent uppercase"
            disabled={!!promoApplied}
          />
          {promoApplied ? (
            <button
              type="button"
              onClick={() => {
                setPromoApplied(null);
                setPromoCode('');
              }}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition"
            >
              Remove
            </button>
          ) : (
            <button
              type="button"
              onClick={validatePromoCode}
              disabled={validating || !promoCode.trim()}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition disabled:opacity-50"
            >
              {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
            </button>
          )}
        </div>
        {promoError && (
          <p className="text-red-600 text-sm mt-1">{promoError}</p>
        )}
        {promoApplied && (
          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            <span><strong>{promoApplied.code}</strong> applied: {promoApplied.discountText}</span>
          </div>
        )}
      </div>

      {/* Pricing Card */}
      <div className="bg-white rounded-2xl border-2 border-slate-200 p-6 mb-6">
        <div className="text-center">
          {promoApplied && discountAmount > 0 ? (
            <>
              <span className="text-2xl text-slate-400 line-through mr-2">${originalPrice.toFixed(2)}</span>
              <span className="text-4xl font-extrabold" style={{ color: brand.navy }}>
                ${displayPrice.toFixed(2)}
              </span>
              <span className="text-slate-500">/month</span>
              {promoApplied.percentOff === 100 && (
                <div className="mt-2 inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                  FREE with promo code!
                </div>
              )}
            </>
          ) : (
            <>
              <span className="text-4xl font-extrabold" style={{ color: brand.navy }}>$49.95</span>
              <span className="text-slate-500">/month</span>
            </>
          )}
        </div>
        <ul className="mt-6 space-y-3">
          {[
            'Unlimited Medicare clients',
            'AI-powered churn prediction',
            'Dedicated phone number',
            'SMS & calling (A2P compliant)',
            'CMS Blue Button integration',
            'HIPAA-compliant & secure'
          ].map((feature, i) => (
            <li key={i} className="flex items-center gap-2 text-slate-700">
              <CheckCircle className="w-5 h-5 text-green-500" />
              {feature}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 rounded-xl font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition flex items-center justify-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handlePayment}
          disabled={loading}
          className="flex-1 py-3 rounded-xl font-bold text-lg transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: brand.gold, color: brand.navy }}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              {displayPrice === 0 ? 'Start Free' : 'Subscribe Now'}
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>

      <p className="text-xs text-center text-slate-500 mt-4">
        Secure payment powered by Stripe. Cancel anytime.
      </p>
    </div>
  );
}

// Phone Choice Step Component - Choose between Pro IRP number or existing CRM
function PhoneChoiceStep({ onComplete, onChooseTwilio, onChooseCRM }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChooseTwilio = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/onboarding/phone-choice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone_source: 'twilio' })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      onChooseTwilio();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChooseCRM = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/onboarding/phone-choice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone_source: 'ghl' })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      onChooseCRM();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <Phone className="w-8 h-8 text-indigo-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">How would you like to handle client communication?</h2>
        <p className="text-slate-600 mt-2">Choose the phone setup that works best for your practice</p>
      </div>

      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Option 1: Pro IRP Phone Number */}
        <button
          onClick={handleChooseTwilio}
          disabled={saving}
          className="w-full p-6 bg-white border-2 border-slate-200 rounded-xl text-left hover:border-indigo-400 hover:shadow-lg transition-all group disabled:opacity-50"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white flex-shrink-0 group-hover:scale-105 transition-transform">
              <Phone className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-800 mb-1">Get a Pro IRP Phone Number</h3>
              <p className="text-slate-600 text-sm mb-3">
                We'll provision a dedicated business phone number for your Medicare practice.
                All texts and calls are managed entirely within Pro IRP.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                  Full control
                </span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  Dedicated number
                </span>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                  A2P compliant
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Best for: Agents without an existing CRM or who want a separate number for Medicare clients.
              </p>
            </div>
          </div>
        </button>

        {/* Option 2: Use Existing CRM Phone */}
        <button
          onClick={handleChooseCRM}
          disabled={saving}
          className="w-full p-6 bg-white border-2 border-slate-200 rounded-xl text-left hover:border-indigo-400 hover:shadow-lg transition-all group disabled:opacity-50"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white flex-shrink-0 group-hover:scale-105 transition-transform">
              <Building2 className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-slate-800 mb-1">
                Use My Existing CRM Phone Number
                <span className="ml-2 text-sm font-normal text-amber-600">(Requires GoHighLevel account)</span>
              </h3>
              <p className="text-slate-600 text-sm mb-3">
                Connect your GoHighLevel account and send texts from your current business number.
                Pro IRP adds automation intelligence while keeping your existing client relationships intact.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                  Keep your number
                </span>
                <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                  GHL integration
                </span>
                <span className="px-2 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full">
                  No duplicate texts
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                Best for: Agents already using GoHighLevel who don't want clients receiving texts from a new number.
              </p>
              <p className="text-xs text-amber-700 mt-1 font-medium">
                After selecting this, connect your GoHighLevel account in Settings → Integrations.
              </p>
            </div>
          </div>
        </button>
      </div>

      <p className="text-center text-xs text-slate-400 mt-6">
        You can change this later in Settings → Integrations
      </p>
    </div>
  );
}

// Twilio Setup Step Component
function TwilioSetupStep({ onComplete }) {
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [startingA2P, setStartingA2P] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [a2pMessage, setA2pMessage] = useState('');

  useEffect(() => {
    fetchStatus();
    // Poll for status updates
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      console.log('[Phone Setup] Fetching twilio status...');
      const res = await fetch(`${BASE}/onboarding/twilio-status`, { credentials: 'include' });
      const json = await res.json();
      console.log('[Phone Setup] Status response:', json);
      if (res.ok) {
        setStatus(json.twilioStatus);
        console.log('[Phone Setup] Status set to:', json.twilioStatus);
        if (json.twilioStatus?.provisioned) {
          setLoading(false);
        }
      }
    } catch (err) {
      console.error('[Phone Setup] Failed to fetch Twilio status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    console.log('[Phone Setup] handleRetry clicked');
    setRetrying(true);
    setError('');

    try {
      console.log('[Phone Setup] Calling /onboarding/twilio/retry...');
      const res = await fetch(`${BASE}/onboarding/twilio/retry`, {
        method: 'POST',
        credentials: 'include'
      });
      const json = await res.json();
      console.log('[Phone Setup] Response:', json);
      if (!res.ok) throw new Error(json.error);
      fetchStatus();
    } catch (err) {
      console.error('[Phone Setup] Error:', err);
      setError(err.message);
    } finally {
      setRetrying(false);
    }
  };

  const handleStartA2P = async () => {
    setStartingA2P(true);
    setError('');
    setA2pMessage('');

    try {
      const res = await fetch(`${BASE}/onboarding/twilio/start-a2p`, {
        method: 'POST',
        credentials: 'include'
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setA2pMessage(json.message || 'A2P registration started successfully!');
      fetchStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setStartingA2P(false);
    }
  };

  // Helper to get A2P status display info with full Tailwind classes
  const getA2PStatusInfo = (a2pStatus) => {
    switch (a2pStatus) {
      case 'approved':
        return {
          label: 'Approved',
          description: 'Your A2P registration is complete. Full SMS throughput enabled.',
          cardClass: 'bg-green-50 border-green-200',
          titleClass: 'text-green-800',
          badgeClass: 'bg-green-100 text-green-700',
          textClass: 'text-green-700'
        };
      case 'pending':
        return {
          label: 'Pending Review',
          description: 'Your registration is being reviewed by carriers. This typically takes 24-48 hours.',
          cardClass: 'bg-yellow-50 border-yellow-200',
          titleClass: 'text-yellow-800',
          badgeClass: 'bg-yellow-100 text-yellow-700',
          textClass: 'text-yellow-700'
        };
      case 'pending_manual':
        return {
          label: 'Action Required',
          description: 'Your Messaging Service is ready. Complete A2P 10DLC registration in Twilio Console to enable full SMS throughput.',
          cardClass: 'bg-blue-50 border-blue-200',
          titleClass: 'text-blue-800',
          badgeClass: 'bg-blue-100 text-blue-700',
          textClass: 'text-blue-700'
        };
      case 'pending_verification':
        return {
          label: 'Pending Verification',
          description: 'Your registration is pending verification. SMS works with limited throughput.',
          cardClass: 'bg-yellow-50 border-yellow-200',
          titleClass: 'text-yellow-800',
          badgeClass: 'bg-yellow-100 text-yellow-700',
          textClass: 'text-yellow-700'
        };
      case 'in_progress':
        return {
          label: 'In Progress',
          description: 'A2P registration is being submitted to carriers.',
          cardClass: 'bg-blue-50 border-blue-200',
          titleClass: 'text-blue-800',
          badgeClass: 'bg-blue-100 text-blue-700',
          textClass: 'text-blue-700'
        };
      case 'failed':
        return {
          label: 'Failed',
          description: 'Registration failed. Please contact support.',
          cardClass: 'bg-red-50 border-red-200',
          titleClass: 'text-red-800',
          badgeClass: 'bg-red-100 text-red-700',
          textClass: 'text-red-700'
        };
      default:
        return {
          label: 'Not Started',
          description: 'A2P registration has not been started yet.',
          cardClass: 'bg-orange-50 border-orange-200',
          titleClass: 'text-orange-800',
          badgeClass: 'bg-orange-100 text-orange-700',
          textClass: 'text-orange-700'
        };
    }
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-slate-800 mb-2">Setting Up Your Phone System</h3>
        <p className="text-slate-600">This usually takes 30-60 seconds...</p>
      </div>
    );
  }

  if (status?.provisioned) {
    const a2pInfo = getA2PStatusInfo(status.a2pStatus);
    // Show button unless A2P is already approved, pending, in progress, or requires manual action
    const hideA2PButton = status.a2pStatus === 'approved' || status.a2pStatus === 'pending' || status.a2pStatus === 'in_progress' || status.a2pStatus === 'pending_manual' || status.a2pStatus === 'pending_verification';

    return (
      <div className="max-w-md mx-auto text-center">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Phone Number Ready!</h2>
        <p className="text-slate-600 mb-6">Your dedicated business line is active.</p>

        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <p className="text-sm text-slate-500 mb-1">Your Business Phone Number</p>
          <p className="text-2xl font-bold" style={{ color: brand.navy }}>{status.phoneNumber}</p>
        </div>

        {/* A2P Registration Status Card */}
        <div className={`${a2pInfo.cardClass} border rounded-lg p-4 mb-6 text-left`}>
          <div className="flex items-center justify-between mb-2">
            <p className={`text-sm font-semibold ${a2pInfo.titleClass}`}>
              A2P 10DLC Registration
            </p>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${a2pInfo.badgeClass}`}>
              {a2pInfo.label}
            </span>
          </div>
          <p className={`text-xs ${a2pInfo.textClass}`}>
            {a2pInfo.description}
          </p>

          {/* What is A2P explanation */}
          <div className="mt-3 pt-3 border-t border-slate-200">
            <p className="text-xs text-slate-600">
              <strong>What is A2P?</strong> Application-to-Person (A2P) registration is required by US carriers for business text messaging.
              It ensures higher deliverability and throughput for your SMS communications.
            </p>
          </div>

          {/* Start A2P Button - shown unless already approved/pending/in_progress */}
          {!hideA2PButton && (
            <div className="mt-4 pt-3 border-t border-slate-200">
              {error && (
                <div className="mb-2 p-2 bg-red-50 border border-red-200 text-red-700 rounded text-xs">
                  {error}
                </div>
              )}
              {a2pMessage && (
                <div className="mb-2 p-2 bg-green-50 border border-green-200 text-green-700 rounded text-xs">
                  {a2pMessage}
                </div>
              )}
              <button
                onClick={handleStartA2P}
                disabled={startingA2P}
                className="w-full py-2 rounded-lg font-medium text-sm bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {startingA2P ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Start A2P Registration
                  </>
                )}
              </button>
            </div>
          )}

          {/* SMS registration in progress */}
          {(status.a2pStatus === 'pending_manual' || status.a2pStatus === 'pending_verification') && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs text-slate-600 mb-1">
                <strong>SMS registration is in progress.</strong> Your phone number is active. You can send messages now, though delivery rates may be slightly lower until registration completes. Full high-volume sending unlocks automatically once carrier registration is verified — this typically takes 1–3 business days. No action needed on your part.
              </p>
              <p className="text-xs text-slate-500">
                We'll notify you when registration is complete.
              </p>
            </div>
          )}

          {/* How to check status */}
          {status.a2pStatus === 'pending' && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <p className="text-xs text-slate-600">
                <strong>Check Status:</strong> You can monitor your A2P registration status in Settings {'>'} Phone Setup.
                We'll also notify you by email when the registration is approved.
              </p>
            </div>
          )}
        </div>

        <button
          onClick={onComplete}
          className="w-full py-3 rounded-xl font-bold text-lg transition hover:opacity-90 flex items-center justify-center gap-2"
          style={{ background: brand.gold, color: brand.navy }}
        >
          Go to Dashboard
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  }

  // Provisioning in progress or failed
  return (
    <div className="max-w-md mx-auto text-center">
      <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
        <Phone className="w-8 h-8 text-blue-600" />
      </div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">Phone Setup</h2>

      {status?.status === 'in_progress' ? (
        <>
          <p className="text-slate-600 mb-6">Setting up your dedicated phone number...</p>
          <div className="flex items-center justify-center gap-3 text-blue-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Processing...</span>
          </div>
        </>
      ) : status?.status === 'failed' ? (
        <>
          <p className="text-red-600 mb-4">{status.error || 'Setup failed. Please try again.'}</p>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="px-6 py-3 rounded-xl font-bold transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
            style={{ background: brand.gold, color: brand.navy }}
          >
            {retrying ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Retrying...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5" />
                Retry Setup
              </>
            )}
          </button>
        </>
      ) : (
        <>
          <p className="text-slate-600 mb-6">Ready to provision your dedicated business phone number.</p>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="px-6 py-3 rounded-xl font-bold transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
            style={{ background: brand.gold, color: brand.navy }}
          >
            {retrying ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Phone className="w-5 h-5" />
                Start Phone Setup
              </>
            )}
          </button>
        </>
      )}

      <button
        onClick={onComplete}
        className="mt-6 text-sm text-slate-500 hover:text-slate-700 underline"
      >
        Skip for now (setup in settings later)
      </button>
    </div>
  );
}

// Available carriers list with integration status
const AVAILABLE_CARRIERS = [
  { id: 'humana', name: 'Humana', integrated: true, hasBookOfBusiness: true },
  { id: 'aetna', name: 'Aetna', integrated: true, hasBookOfBusiness: false },
  { id: 'anthem', name: 'Anthem / Elevance', integrated: true, hasBookOfBusiness: false },
  { id: 'unitedhealthcare', name: 'UnitedHealthcare', integrated: false, hasBookOfBusiness: false },
  { id: 'cigna', name: 'Cigna', integrated: false, hasBookOfBusiness: false },
  { id: 'wellcare', name: 'Wellcare / Centene', integrated: false, hasBookOfBusiness: false },
  { id: 'molina', name: 'Molina Healthcare', integrated: false, hasBookOfBusiness: false },
  { id: 'kaiser', name: 'Kaiser Permanente', integrated: false, hasBookOfBusiness: false },
  { id: 'caresource', name: 'CareSource', integrated: false, hasBookOfBusiness: false },
  { id: 'devoted', name: 'Devoted Health', integrated: false, hasBookOfBusiness: false },
  { id: 'clover', name: 'Clover Health', integrated: false, hasBookOfBusiness: false },
  { id: 'oscar', name: 'Oscar Health', integrated: false, hasBookOfBusiness: false },
  { id: 'bcbs_florida', name: 'Florida Blue (BCBS)', integrated: false, hasBookOfBusiness: false },
  { id: 'bcbs_michigan', name: 'BCBS Michigan', integrated: false, hasBookOfBusiness: false },
  { id: 'bcbs_texas', name: 'BCBS Texas', integrated: false, hasBookOfBusiness: false },
  { id: 'highmark', name: 'Highmark BCBS', integrated: false, hasBookOfBusiness: false },
  { id: 'independence', name: 'Independence Blue Cross', integrated: false, hasBookOfBusiness: false },
  { id: 'horizon', name: 'Horizon BCBS (NJ)', integrated: false, hasBookOfBusiness: false },
  { id: 'scan', name: 'SCAN Health Plan', integrated: false, hasBookOfBusiness: false },
  { id: 'alignment', name: 'Alignment Healthcare', integrated: false, hasBookOfBusiness: false },
  { id: 'other', name: 'Other', integrated: false, hasBookOfBusiness: false },
];

// Book of Business Step Component
function BookOfBusinessStep({ onComplete, onBack }) {
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [npn, setNpn] = useState('');
  const [selectedCarriers, setSelectedCarriers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncResults, setSyncResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const filteredCarriers = AVAILABLE_CARRIERS.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleCarrier = (carrierId) => {
    setSelectedCarriers(prev =>
      prev.includes(carrierId)
        ? prev.filter(id => id !== carrierId)
        : [...prev, carrierId]
    );
  };

  const handleSync = async () => {
    if (!npn || npn.length < 7) {
      setError('Please enter a valid NPN number (at least 7 digits)');
      return;
    }

    if (selectedCarriers.length === 0) {
      setError('Please select at least one carrier');
      return;
    }

    setSyncing(true);
    setError('');

    try {
      const res = await fetch(`${BASE}/onboarding/book-of-business`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          npn: npn.replace(/\D/g, ''),
          carriers: selectedCarriers
        })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);

      setSyncResults(json.results);
      setShowResults(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleContinue = async () => {
    setSaving(true);
    try {
      // Save NPN even if no sync was done
      if (npn && !syncResults) {
        await fetch(`${BASE}/onboarding/save-npn`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ npn: npn.replace(/\D/g, '') })
        });
      }
      onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (showResults && syncResults) {
    const successCount = syncResults.filter(r => r.status === 'success').length;
    const manualCount = syncResults.filter(r => r.status === 'manual_required').length;
    const totalClients = syncResults.reduce((sum, r) => sum + (r.clientsImported || 0), 0);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Book of Business Sync Complete</h2>
          <p className="text-slate-600 mt-2">
            {totalClients > 0 ? `Imported ${totalClients} clients from ${successCount} carrier${successCount !== 1 ? 's' : ''}` : 'Sync attempted for selected carriers'}
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {syncResults.map((result) => {
            const carrier = AVAILABLE_CARRIERS.find(c => c.id === result.carrierId);
            return (
              <div
                key={result.carrierId}
                className={`p-4 rounded-xl border-2 flex items-center justify-between ${
                  result.status === 'success'
                    ? 'bg-green-50 border-green-200'
                    : result.status === 'oauth_required'
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-orange-50 border-orange-200'
                }`}
              >
                <div>
                  <div className="font-medium text-slate-800">{carrier?.name || result.carrierId}</div>
                  <div className={`text-sm ${
                    result.status === 'success' ? 'text-green-600' :
                    result.status === 'oauth_required' ? 'text-blue-600' : 'text-orange-600'
                  }`}>
                    {result.status === 'success' && `${result.clientsImported} clients imported`}
                    {result.status === 'oauth_required' && 'Requires login - connect in Settings'}
                    {result.status === 'manual_required' && 'API not available - import via CSV'}
                    {result.status === 'error' && (result.message || 'Sync failed')}
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  result.status === 'success' ? 'bg-green-100 text-green-700' :
                  result.status === 'oauth_required' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                }`}>
                  {result.status === 'success' ? 'Synced' :
                   result.status === 'oauth_required' ? 'Connect' : 'Manual'}
                </div>
              </div>
            );
          })}
        </div>

        {manualCount > 0 && (
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-sm text-slate-600">
            <p className="font-medium text-slate-700 mb-1">Need to import manually?</p>
            <p>Go to Clients → Import to upload a CSV file for carriers without API integration.</p>
          </div>
        )}

        <button
          onClick={handleContinue}
          disabled={saving}
          className="w-full py-3 rounded-xl font-bold text-lg transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: brand.gold, color: brand.navy }}
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Continue
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <Briefcase className="w-8 h-8 text-indigo-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800">Connect Your Book of Business</h2>
        <p className="text-slate-600 mt-2">Import your clients from carrier portals automatically</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* NPN Input */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Your NPN (National Producer Number) *
        </label>
        <input
          type="text"
          value={npn}
          onChange={(e) => setNpn(e.target.value.replace(/\D/g, '').slice(0, 10))}
          placeholder="Enter your 10-digit NPN"
          className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-400 focus:border-transparent text-lg font-mono"
          maxLength={10}
        />
        <p className="text-xs text-slate-500 mt-1">
          Find your NPN at <a href="https://nipr.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">nipr.com</a>
        </p>
      </div>

      {/* Carrier Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-slate-700 mb-2">
          Select Your Carriers
        </label>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search carriers..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
          />
        </div>

        {/* Selected carriers tags */}
        {selectedCarriers.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {selectedCarriers.map(id => {
              const carrier = AVAILABLE_CARRIERS.find(c => c.id === id);
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                >
                  {carrier?.name}
                  <button onClick={() => toggleCarrier(id)} className="hover:text-indigo-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* Carrier list */}
        <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-xl">
          {filteredCarriers.map((carrier) => {
            const isSelected = selectedCarriers.includes(carrier.id);
            return (
              <button
                key={carrier.id}
                type="button"
                onClick={() => toggleCarrier(carrier.id)}
                className={`w-full px-4 py-3 flex items-center justify-between text-left border-b border-slate-100 last:border-0 transition ${
                  isSelected ? 'bg-indigo-50' : 'hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'
                  }`}>
                    {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  <span className="font-medium text-slate-800">{carrier.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  {carrier.hasBookOfBusiness && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Auto-sync
                    </span>
                  )}
                  {carrier.integrated && !carrier.hasBookOfBusiness && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                      Formulary
                    </span>
                  )}
                  {!carrier.integrated && (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">
                      Manual import
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-slate-50 rounded-xl p-4 mb-6 text-xs text-slate-600 space-y-1">
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Auto-sync</span>
          <span>Clients automatically imported from carrier</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">Formulary</span>
          <span>Drug tier & cost info available for clients</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">Manual import</span>
          <span>Import clients via CSV upload</span>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-3 rounded-xl font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 transition flex items-center justify-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={handleSync}
          disabled={syncing || !npn || selectedCarriers.length === 0}
          className="flex-1 py-3 rounded-xl font-bold text-lg transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: brand.gold, color: brand.navy }}
        >
          {syncing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              Sync Book of Business
              <ChevronRight className="w-5 h-5" />
            </>
          )}
        </button>
      </div>

      <button
        onClick={handleContinue}
        className="w-full mt-4 text-sm text-slate-500 hover:text-slate-700 underline"
      >
        Skip for now (import clients later)
      </button>
    </div>
  );
}

// ─── Step 5: Import Clients ───────────────────────────────────────────────────
function ImportClientsStep({ onComplete }) {
  const navigate = useNavigate();

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#EFF6FF' }}>
          <Upload className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold" style={{ color: brand.navy }}>Import Your Clients</h2>
          <p className="text-sm text-slate-500">Step 5 of 7</p>
        </div>
      </div>

      <p className="text-slate-600 mb-6">
        Importing your existing book of business unlocks Pro IRP's most powerful features —
        risk scoring, automated outreach, OEP cohort tracking, and retention analytics.
        The import tool accepts CSV and Excel files and maps your columns automatically.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        {[
          { icon: '📊', label: 'Risk scoring on all clients immediately' },
          { icon: '🤖', label: 'Birthday & renewal automations activate' },
          { icon: '📋', label: 'OEP cohort built from effective dates' },
        ].map(({ icon, label }) => (
          <div key={label} className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
            <span className="text-base">{icon}</span>
            <span>{label}</span>
          </div>
        ))}
      </div>

      <button
        onClick={() => navigate(ONBOARDING_STEP_ROUTES['import_clients'])}
        className="w-full py-3 rounded-xl font-bold text-lg transition hover:opacity-90 flex items-center justify-center gap-2 mb-3"
        style={{ background: brand.navy, color: '#fff' }}
      >
        <Upload className="w-5 h-5" />
        Launch Import Tool
      </button>

      <button
        onClick={onComplete}
        className="w-full mt-1 text-sm text-slate-500 hover:text-slate-700 underline"
      >
        Skip for now (you'll need to add clients to unlock features)
      </button>
    </div>
  );
}

// ─── Step 6: Review Automations ───────────────────────────────────────────────
function ReviewAutomationsStep({ onComplete }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const AUTOMATIONS = [
    {
      icon: '🎂',
      title: 'Birthday Message',
      description: 'Sends a personalized SMS to each client on their birthday.',
    },
    {
      icon: '📅',
      title: 'Renewal Reminder',
      description: 'Reminds clients 60 and 30 days before their plan renewal date.',
    },
    {
      icon: '📣',
      title: 'AEP Outreach',
      description: 'Reaches out to your book in October with an annual review prompt.',
    },
    {
      icon: '🔄',
      title: 'Re-engagement',
      description: 'Flags and messages clients who haven\'t been contacted in 90+ days.',
    },
  ];

  async function handleMarkReviewed() {
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${BASE}/onboarding/automations-reviewed`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to save');
      }
      setSaved(true);
      setTimeout(() => onComplete(), 600);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: '#F0FDF4' }}>
          <ToggleRight className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold" style={{ color: brand.navy }}>Review Your Automations</h2>
          <p className="text-sm text-slate-500">Step 6 of 7</p>
        </div>
      </div>

      <p className="text-slate-600 mb-6">
        Pro IRP comes pre-configured with smart automations for your book of business.
        These are enabled by default — take a moment to confirm they look right for your practice.
      </p>

      <div className="space-y-3 mb-6">
        {AUTOMATIONS.map(({ icon, title, description }) => (
          <div key={title} className="flex items-start gap-3 p-4 border border-slate-200 rounded-xl">
            <span className="text-2xl">{icon}</span>
            <div>
              <div className="font-semibold text-slate-800 text-sm">{title}</div>
              <div className="text-slate-500 text-sm">{description}</div>
            </div>
            <div className="ml-auto flex-shrink-0">
              <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Active</span>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => navigate(ONBOARDING_STEP_ROUTES['review_automations'])}
        className="text-sm text-blue-600 hover:underline mb-5 block"
      >
        Customize automations →
      </button>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <button
        onClick={handleMarkReviewed}
        disabled={saving || saved}
        className="w-full py-3 rounded-xl font-bold text-lg transition hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
        style={{ background: saved ? '#16a34a' : brand.navy, color: '#fff' }}
      >
        {saved ? (
          <><CheckCircle className="w-5 h-5" /> Saved!</>
        ) : saving ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Saving…</>
        ) : (
          <><Settings2 className="w-5 h-5" /> Looks good — Mark as reviewed</>
        )}
      </button>
    </div>
  );
}

// ─── Step 7: Completion ───────────────────────────────────────────────────────
function CompletionStep({ onDone }) {
  return (
    <div className="text-center py-6">
      <div className="flex justify-center mb-6">
        <div className="w-20 h-20 rounded-full flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #FFB800 0%, #f59e0b 100%)' }}>
          <Rocket className="w-10 h-10 text-white" />
        </div>
      </div>

      <h2 className="text-3xl font-bold mb-3" style={{ color: brand.navy }}>You're all set!</h2>
      <p className="text-slate-600 mb-8 max-w-sm mx-auto">
        Your account is fully configured. Here's what's waiting for you:
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 text-left">
        {[
          { icon: '📊', title: 'Risk Dashboard', description: 'AI-scored clients, at-risk alerts, and retention insights.' },
          { icon: '💬', title: 'Automated Outreach', description: 'Birthdays, renewals, and AEP messages send on autopilot.' },
          { icon: '📋', title: 'OEP Cohort', description: 'Your open enrollment pipeline is built and ready to work.' },
        ].map(({ icon, title, description }) => (
          <div key={title} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="text-2xl mb-2">{icon}</div>
            <div className="font-semibold text-slate-800 text-sm mb-1">{title}</div>
            <div className="text-slate-500 text-xs">{description}</div>
          </div>
        ))}
      </div>

      <button
        onClick={onDone}
        className="w-full py-3 rounded-xl font-bold text-lg transition hover:opacity-90 flex items-center justify-center gap-2"
        style={{ background: brand.navy, color: '#fff' }}
      >
        <Rocket className="w-5 h-5" />
        Go to your dashboard
      </button>
    </div>
  );
}

// Main Onboarding Component
export default function Onboarding() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState('baa');
  const [error, setError] = useState('');
  const [phoneSource, setPhoneSource] = useState(null); // 'twilio' or 'ghl'
  const [userRole, setUserRole] = useState(null);

  const AGENCY_ROLES = new Set(['agency', 'agency_owner', 'agency_admin']);
  const getDashboardPath = (role) => AGENCY_ROLES.has(role) ? '/agency/dashboard' : '/dashboard';

  const steps = ['baa', 'business_info', 'payment', 'phone_choice', 'twilio_setup', 'book_of_business', 'import_clients', 'review_automations', 'completion'];

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const [statusRes, meRes] = await Promise.all([
        fetch(`${BASE}/onboarding/status`, { credentials: 'include' }),
        fetch(`${BASE}/auth/me`, { credentials: 'include' })
      ]);

      if (!statusRes.ok) {
        if (statusRes.status === 401) {
          navigate('/login');
          return;
        }
        const json = await statusRes.json();
        throw new Error(json.error);
      }

      const json = await statusRes.json();
      const meJson = await meRes.json();
      const role = meJson?.user?.role || null;
      setUserRole(role);

      const status = json.status;

      // Determine current step — hard gates handled by backend currentStep;
      // soft gates (import_clients, review_automations) use nextRequiredStep
      if (status.currentStep === 'completed') {
        const nrs = status.nextRequiredStep;
        if (nrs === 'import_clients' || nrs === 'review_automations') {
          setCurrentStep(nrs);
        } else {
          // Fully done — go to dashboard
          navigate(getDashboardPath(role));
        }
        return;
      }

      setCurrentStep(status.currentStep);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStepComplete = (step) => {
    const stepIndex = steps.indexOf(step);
    if (stepIndex < steps.length - 1) {
      setCurrentStep(steps[stepIndex + 1]);
    } else {
      navigate(getDashboardPath(userRole));
    }
  };

  const handleBack = () => {
    const stepIndex = steps.indexOf(currentStep);
    if (stepIndex > 0) {
      setCurrentStep(steps[stepIndex - 1]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: `linear-gradient(135deg, #1f3550 0%, #1672d6 100%)` }}>
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Pro IRP" className="w-10 h-10 rounded-full bg-white shadow" />
            <span className="font-bold text-xl" style={{ color: brand.navy }}>
              Pro <span style={{ color: brand.gold }}>IRP</span>
            </span>
          </div>
          <span className="text-sm text-slate-500">Getting Started</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-10">
        <StepIndicator steps={steps} currentStep={currentStep} />

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          {currentStep === 'baa' && (
            <BAAStep onComplete={() => handleStepComplete('baa')} />
          )}
          {currentStep === 'business_info' && (
            <BusinessInfoStep
              onComplete={() => handleStepComplete('business_info')}
              onBack={handleBack}
            />
          )}
          {currentStep === 'payment' && (
            <PaymentStep
              onComplete={() => handleStepComplete('payment')}
              onBack={handleBack}
            />
          )}
          {currentStep === 'phone_choice' && (
            <PhoneChoiceStep
              onChooseTwilio={() => {
                setPhoneSource('twilio');
                setCurrentStep('twilio_setup');
              }}
              onChooseCRM={() => {
                setPhoneSource('ghl');
                setCurrentStep('book_of_business');
              }}
            />
          )}
          {currentStep === 'twilio_setup' && (
            <TwilioSetupStep onComplete={() => setCurrentStep('book_of_business')} />
          )}
          {currentStep === 'book_of_business' && (
            <BookOfBusinessStep
              onComplete={() => handleStepComplete('book_of_business')}
              onBack={() => setCurrentStep('phone_choice')}
            />
          )}
          {currentStep === 'import_clients' && (
            <ImportClientsStep onComplete={() => handleStepComplete('import_clients')} />
          )}
          {currentStep === 'review_automations' && (
            <ReviewAutomationsStep onComplete={() => handleStepComplete('review_automations')} />
          )}
          {currentStep === 'completion' && (
            <CompletionStep onDone={() => navigate(getDashboardPath(userRole))} />
          )}
        </div>
      </main>
    </div>
  );
}
